import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const featuredArticles = articles.filter((a) => a.isFeatured).slice(0, 3);
  const hotArticles = articles.filter((a) => a.isHot && a.category !== slug).slice(0, 5);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">分类未找到</h1>
          <p className="text-foreground-600 mb-6">您访问的分类不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${category.name} - 17吃瓜`;
  const pageDescription = category.description;
  const pageKeywords = `${category.name},17吃瓜,吃瓜热点,娱乐爆料,明星八卦`;

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://17chigua.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://17chigua.com/category/${slug}`}
        schema={collectionPageSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            {/* Category Header */}
            <section className="mb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl text-accent-600`}></i>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground-950">{category.name}</h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共找到 <strong className="text-primary-500">{categoryArticles.length}</strong> 篇相关爆料
                  </p>
                </div>
              </div>
              <p className="text-foreground-600 max-w-2xl leading-relaxed">{category.description}</p>
            </section>

            {/* Category Tabs */}
            <section className="mb-10">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/"
                  className="px-4 py-2 text-sm font-medium rounded-lg text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </section>

            {/* Main Content */}
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                {categoryArticles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {categoryArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-600">暂无该分类的爆料内容</p>
                  </div>
                )}
              </div>

              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <h3 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-star-line text-primary-500"></i>
                      精选推荐
                    </h3>
                    <div className="space-y-1">
                      {featuredArticles.map((article) => (
                        <ArticleCard key={article.id} article={article} variant="compact" />
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <h3 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-fire-line text-primary-500"></i>
                      热门爆料
                    </h3>
                    <div className="space-y-1">
                      {hotArticles.map((article) => (
                        <ArticleCard key={article.id} article={article} variant="compact" />
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}